// @ts-check
/**
 * @typedef {Object} Product
 * @property {string} key - The unique identifier for the product.
 * @property {string} title - The product title.
 * @property {string} ticket - The elevate notify ID.
 * @property {string} link - The URL to the product page.
 */

import "./elevate-cart.js";

class Elevate extends HTMLElement {
  static get observedAttributes() {
    return ["page", "collection-id", "product-id"];
  }

  /** @type {string | null} */
  page = null;

  /** @type {string | null} */
  collectionId = null;

  /** @type {string | null} */
  productId = null;

  /** @type {Product | null} */
  product = null;

  /** @type {Product[]} */
  products = [];

  constructor() {
    super();
  }

  connectedCallback() {
    this.page = this.getAttribute("page");
    this.collectionId = this.getAttribute("collection-id");
    this.productId = this.getAttribute("product-id");

    document.addEventListener("DOMContentLoaded", () => {
      // @ts-ignore
      if (!window.elevate) {
        return;
      }

      this.fetchElevateData();
      this.setupListeners();
    });
  }

  async fetchElevateData() {
    if (this.page === "product" && this.productId) {
      // @ts-ignore
      const results = await window.elevate.query.productPage(
        {
          productKey: this.productId,
        },
        {
          productGroup: {
            include: true,
          },
        },
      );

      this.product = results?.productGroup?.products?.find(
        (/** @type {any} */ p) => p.key === this.productId,
      );
      return;
    }
  }

  setupListeners() {
    document.addEventListener("elevate:updateProducts", (event) => {
      const products = /** @type {CustomEvent} */ (event).detail.products;
      if (products && products.length > 0) {
        for (const product of products) {
          this.products.push(product);
        }
      }
    });

    // Listen for cart events and update Elevate attributes
    document.addEventListener("cart:change", async (event) => {
      const cart = /** @type {CustomEvent} */ (event).detail.cart;

      const { _elevate_customer_key, _elevate_session_key, _elevate_market } =
        cart.attributes || {};
      if (_elevate_customer_key && _elevate_session_key && _elevate_market) {
        return;
      }

      // @ts-ignore
      const { customerKey, sessionKey } = window.elevate.query.c.session();

      // @ts-ignore
      const elevateMarket = window.elevate.query.c.market;

      await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          attributes: {
            _elevate_customer_key: customerKey,
            _elevate_session_key: sessionKey,
            _elevate_market: elevateMarket,
          },
          items: [],
        }),
      });
    });

    // Listen for add to cart clicks and notify Elevate
    document.addEventListener("click", (event) => {
      const target = event.target;
      if (!target || !(target instanceof Element)) {
        return;
      }

      if (
        target.getAttribute("aria-label") === "Add to cart" &&
        target.hasAttribute("data-product-id")
      ) {
        // Register add to cart click
        const clickedProduct =
          this.product?.key === target.getAttribute("data-product-id")
            ? this.product
            : this.products.find(
                (p) => p.key === target.getAttribute("data-product-id"),
              );

        if (clickedProduct?.ticket) {
          // @ts-ignore
          window.elevate.notify.addToCart(clickedProduct.ticket);
          return;
        }
      } else {
        // Register product link clicks
        const anchor = target.nodeName === "A" ? target : target.closest("a");
        const productId = anchor?.getAttribute("data-product-id");
        if (!productId) {
          return;
        }

        const clickedProduct =
          this.product?.key === productId
            ? this.product
            : this.products.find((p) => p.key === productId);

        if (clickedProduct?.ticket) {
          // @ts-ignore
          window.elevate.notify.click(clickedProduct.ticket);
        }
      }
    });

    // Wishlist events
    document.addEventListener("wk:wishlist:add:success", (event) => {
      const productId = /** @type {CustomEvent} */ (event).detail?.data?.product
        ?.id;

      if (productId) {
        // @ts-ignore
        window.elevate.notify.addFavorite({ productKey: productId });
      }
    });

    // Wishlist events
    document.addEventListener("wk:wishlist:remove:success", (event) => {
      const productId = /** @type {CustomEvent} */ (event).detail?.data?.product
        ?.id;
      if (productId) {
        // @ts-ignore
        window.elevate.notify.removeFavorite({ productKey: productId });
      }
    });
  }
}

if (!window.customElements.get("elevate-overlay")) {
  customElements.define("elevate-overlay", Elevate);
}
